import { useTranslation } from 'react-i18next';
import useSound from 'use-sound';
import { useSessionStorage } from 'usehooks-ts';
import MessageSound from '@/assets/sounds/ding.mp3?url';
import AppCell from '@/components/common/app-cell';
import SectionHeader from '@/components/common/section-header';
import Toggle from '@/components/common/toggle';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useNotificationSettings from '@/hooks/settings/use-notification-settings';
import useUpdateNotificationSettings from '@/hooks/settings/use-update-notification-settings';
import useUserPlatform from '@/hooks/use-user-platform';
import type { NotificationSettings } from '@/models/mine/notification-setting';
import { TRIGGER_SOUND_STORAGE_KEY } from '@/utils/constants';

export default function NotificationSettingsRoute() {
  const { isDesktop } = useUserPlatform();
  const { t } = useTranslation();
  const { data: notificationSettings } = useNotificationSettings();
  const { updateNotificationSettings, isPending } = useUpdateNotificationSettings();
  const [, setTriggerSound] = useSessionStorage(TRIGGER_SOUND_STORAGE_KEY, false);
  const [playMessageSound] = useSound(MessageSound);

  const handleUpdateNotificationSetting =
    (key: keyof NotificationSettings, isReversed = false) =>
    (value: boolean) => {
      // NOTE: 後端有些config變數邏輯與前端相反  須留意
      updateNotificationSettings(key, isReversed ? value : !value);
    };

  return (
    <PageLayout
      title={t('mine.notifications')}
      headerConfig={{
        navigateBack: !isDesktop,
      }}>
      <div className='flex flex-col'>
        <AppCell
          label={t('notification-settings.mute')}
          content={
            <Toggle
              checked={notificationSettings?.IsNotDisturbAll}
              isLoading={isPending}
              onChange={handleUpdateNotificationSetting('IsNotDisturbAll', true)}
            />
          }
        />

        {!notificationSettings?.IsNotDisturbAll && (
          <>
            <SectionHeader
              className='mx-5'
              letter={t('notification-settings.is-closed')}
            />

            <AppCell
              label={t('notification-settings.push-notification')}
              content={
                <Toggle
                  checked={!notificationSettings?.IsNotMessageDisturb}
                  isLoading={isPending}
                  onChange={handleUpdateNotificationSetting('IsNotMessageDisturb')}
                />
              }
            />
            {/* 暫時隱藏以下選項 */}
            {/* <AppCell
            label={t('notification-settings.voice-invitation')}
            suffix={
              <Toggle
                checked={!notificationSettings?.IsNotCallDisturb}
                onChange={handleUpdateNotificationSetting('IsNotCallDisturb')}
              />
            }
          /> */}

            <SectionHeader
              className='mx-4'
              letter={t('notification-settings.is-open')}
            />

            <AppCell
              label={t('notification-settings.sound')}
              content={
                <Toggle
                  checked={!notificationSettings?.IsNotSound}
                  onChange={(value) => {
                    if (value) {
                      playMessageSound();
                      setTriggerSound(true);
                    }
                    handleUpdateNotificationSetting('IsNotSound')(value);
                  }}
                />
              }
            />
            <AppCell
              label={t('notification-settings.vibrate')}
              className='platform-ios:hidden'
              content={
                <Toggle
                  checked={!notificationSettings?.IsNotVibration}
                  onChange={handleUpdateNotificationSetting('IsNotVibration')}
                />
              }
            />
          </>
        )}
      </div>
    </PageLayout>
  );
}
